import type {
  EducationCartItem,
  StoredEducationCart,
} from './education-cart.type';

const STORAGE_KEY = 'education:cart';
const EMPTY_CART: StoredEducationCart = { ownerId: null, items: [] };

function isCartItem(value: unknown): value is EducationCartItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;

  return (
    typeof item.sessionId === 'string' &&
    typeof item.courseId === 'string' &&
    typeof item.courseTitle === 'string' &&
    typeof item.sessionTitle === 'string' &&
    typeof item.startAt === 'string' &&
    (item.deliveryMode === 'ONLINE' || item.deliveryMode === 'OFFLINE') &&
    typeof item.price === 'number' &&
    Number.isFinite(item.price)
  );
}

/** sessionStorage 접근 실패나 손상된 값은 빈 장바구니로 취급한다. */
export function readEducationCart(): StoredEducationCart {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_CART;

    const parsed: unknown = JSON.parse(raw);

    // 이전 저장 형식은 항목 배열만 담았다. 로그인 전 장바구니로 안전하게 승격한다.
    if (Array.isArray(parsed)) {
      return { ownerId: null, items: parsed.filter(isCartItem) };
    }

    if (!parsed || typeof parsed !== 'object') return EMPTY_CART;
    const stored = parsed as Record<string, unknown>;
    if (stored.ownerId !== null && typeof stored.ownerId !== 'string') {
      return EMPTY_CART;
    }
    if (!Array.isArray(stored.items)) return EMPTY_CART;

    return {
      ownerId: stored.ownerId,
      items: stored.items.filter(isCartItem),
    };
  } catch {
    return EMPTY_CART;
  }
}

export function writeEducationCart(
  ownerId: string | null,
  items: EducationCartItem[],
): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ownerId, items }));
  } catch {
    // 브라우저 설정으로 storage 를 쓸 수 없어도 현재 화면의 React 상태는 유지한다.
  }
}

export function clearEducationCart(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage 접근 불가 환경에서는 정리할 값에도 접근할 수 없으므로 무시한다.
  }
}
